// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import type {IARContext, IARResolvedContext} from '@app/features/devtools/components/modals/IARModal.types';
import Relationships from '@app/features/relationship/state/Relationships';
import type {User} from '@app/features/user/models/User';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

function getModalTitle(i18n: I18n, context: IARContext): string {
	switch (context.type) {
		case 'message':
			return i18n._(msg`Report Message`);
		case 'user':
			return i18n._(msg`Report User`);
		case 'guild':
			return i18n._(msg`Report Community`);
	}
}

function getSubjectLabel(i18n: I18n, context: IARContext): string {
	switch (context.type) {
		case 'message':
			return i18n._(msg`message`);
		case 'user':
			return i18n._(msg`user`);
		case 'guild':
			return i18n._(msg`community`);
	}
}

function getCurrentChannel(context: IARContext): Channel | null {
	switch (context.type) {
		case 'message':
			return Channels.getChannel(context.message.channelId) ?? null;
		case 'user':
		case 'guild':
			return null;
	}
}

function getReportedUser(context: IARContext, currentUserId: string | null): User | null {
	switch (context.type) {
		case 'message':
			if (context.message.author.id === currentUserId) {
				return null;
			}

			return context.message.author;
		case 'user':
			if (context.user.id === currentUserId) {
				return null;
			}

			return context.user;
		case 'guild':
			return null;
	}
}

function getLeaveableGuildId(context: IARContext, currentChannel: Channel | null): string | null {
	switch (context.type) {
		case 'guild':
			return context.guild.id;
		case 'user':
			return context.guildId ?? null;
		case 'message':
			if (currentChannel?.guildId) {
				return currentChannel.guildId;
			}

			return context.message.guildId ?? null;
	}
}

function getDMChannel(context: IARContext, currentChannel: Channel | null, reportedUser: User | null): Channel | null {
	switch (context.type) {
		case 'message':
			if (currentChannel?.isDM()) {
				return currentChannel;
			}

			break;
		case 'user':
			break;
		case 'guild':
			return null;
	}

	if (reportedUser === null) {
		return null;
	}

	const dmChannel =
		Channels.dmChannels.find((channel) => channel.isDM() && channel.getRecipientId() === reportedUser.id) ?? null;

	return dmChannel;
}

function getDMDisplayName(i18n: I18n, reportedUser: User | null): string {
	if (reportedUser !== null) {
		return reportedUser.username;
	}

	return i18n._(msg`this user`);
}

export function resolveIARModalContext(i18n: I18n, context: IARContext): IARResolvedContext {
	const currentChannel = getCurrentChannel(context);
	const reportedUser = getReportedUser(context, Authentication.currentUserId);
	const leaveableGuildId = getLeaveableGuildId(context, currentChannel);
	const dmChannel = getDMChannel(context, currentChannel, reportedUser);

	return {
		title: getModalTitle(i18n, context),
		subjectLabel: getSubjectLabel(i18n, context),
		currentChannel,
		reportedUser,
		isReportedUserBlocked: reportedUser !== null && Relationships.isBlocked(reportedUser.id),
		leaveableGuildId,
		hasCommunityContext: context.type !== 'guild' && leaveableGuildId !== null,
		dmChannel,
		dmDisplayName: getDMDisplayName(i18n, reportedUser),
	};
}
